import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  // Nothing to hydrate (e.g. header menu disabled) — skip fetch/parse entirely.
  if (!section.querySelector('[data-hydration-key]')) {
    section.dataset.hydrated = 'true';
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrate only when the user is about to need the heavy menu content.
 * Eager idle hydration was freezing scroll + background animation ~1–2s after load.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    const normalizedId = normalizeSectionId(sectionId);
    const section = document.getElementById(buildSectionSelector(normalizedId));

    if (!section || section.dataset.hydrated === 'true') {
      return;
    }

    if (!section.querySelector('[data-hydration-key]')) {
      section.dataset.hydrated = 'true';
      return;
    }

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      section.removeEventListener('pointerenter', start);
      section.removeEventListener('focusin', start);
      section.removeEventListener('touchstart', start);
      hydrateSection(sectionId, url);
    };

    section.addEventListener('pointerenter', start, { passive: true });
    section.addEventListener('focusin', start, { passive: true });
    section.addEventListener('touchstart', start, { passive: true });
  });
}
